import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import DateRangeText from 'domains/core/components/DateRangeText';
import Table, { ColumnOption } from 'domains/core/components/Table';
import MutationKeys from 'hooks/mutations/keys';
import useMutationStatus from 'hooks/useMutationStatus';
import { CampaignStatus, MutationStatus, URLPaths } from 'models/enums';
import {
    Campaign,
    CampaignChannelLabels,
    CampaignChannels,
    PartialSend,
    SendRecurrenceFrequency,
} from 'domains/campaigns/types';
import CampaignStatusText from './CampaignStatusText';
import Recurrence from 'domains/campaigns/components/Recurrence/Recurrence';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import useGetCampaignMenuItem from './useGetCampaignMenuItem';
import ConfirmationDialog from 'domains/core/components/ConfirmationDialog';
import useDeleteCampaign from 'hooks/mutations/useDeleteCampaign';
import useUpdateCampaignStatus from 'hooks/mutations/useUpdateCampaignStatus';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import { useFlags } from 'launchdarkly-react-client-sdk';

export type Props = {
    campaigns: Campaign<PartialSend>[];
    isRefetching: boolean;
    searchTerm?: string;
    refetch: () => void;
};

/**
 * A table used for displaying curations in the curations page.
 *
 * @param campaigns The campaigns data passed in from CurationsPage.
 * @param isRefetching Whether the campaigns data is currently being refetched.
 * @param refetch Function to refetch the campaigns data.
 * @returns The React node created by this component.
 */
const CampaignsTable = ({ campaigns, isRefetching, searchTerm, refetch }: Props) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [campaign, setCampaign] = useState<Campaign>();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [pendingEditCampaign, setPendingEditCampaign] = useState<{ id: string; name: string } | null>(null);
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();

    const history = useHistory();

    const deleteCampaignStatus = useMutationStatus(MutationKeys.DELETE_CAMPAIGN);
    const updateCampaignStatusStatus = useMutationStatus(MutationKeys.UPDATE_CAMPAIGN_STATUS);

    const { mutate: deleteCampaign, isLoading: isDeleteLoading } = useDeleteCampaign(campaign?.id, {
        onError: (error: Error) => setErrorMessage(error.message),
        onSuccess: () => {
            setSuccessMessage(`"${campaign.name}" successfully deleted.`);
            setInfoMessage('');
            refetch();
        },
    });
    const { mutate: pauseCampaign, isLoading: isPauseLoading } = useUpdateCampaignStatus(
        campaign?.id,
        CampaignStatus.PAUSED.toLowerCase(),
        {
            onError: (error: Error) => setErrorMessage(error.message),
            onSuccess: () => {
                setSuccessMessage(`"${campaign?.name}" paused.`);
                setInfoMessage('');
                refetch();
            },
        }
    );
    const { mutate: resumeCampaign, isLoading: isResumeLoading } = useUpdateCampaignStatus(
        campaign?.id,
        CampaignStatus.RESUMED.toLowerCase(),
        {
            onError: (error: Error) => setErrorMessage(error.message),
            onSuccess: () => {
                setSuccessMessage(`"${campaign?.name}" resumed.`);
                setInfoMessage('');
                refetch();
            },
        }
    );

    // Hook for converting scheduled curation to draft and then editing
    const { mutate: convertToDraftAndEdit, isLoading: isConvertingToDraft } = useUpdateCampaignStatus(
        pendingEditCampaign?.id,
        CampaignStatus.DRAFT.toLowerCase(),
        {
            onError: (error: Error) => {
                setErrorMessage(error.message);
                setInfoMessage('');
                setPendingEditCampaign(null);
            },
            onSuccess: () => {
                setInfoMessage('');
                // Navigate to edit page after successful conversion
                if (pendingEditCampaign) {
                    history.push(
                        `${URLPaths.CURATIONS_CUSTOM}/${pendingEditCampaign.id}?status=${CampaignStatus.DRAFT}`
                    );
                }
                setPendingEditCampaign(null);
            },
        }
    );

    // Trigger the convert to draft mutation when pendingEditCampaign is set
    useEffect(() => {
        if (pendingEditCampaign?.id) {
            convertToDraftAndEdit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingEditCampaign?.id]);

    const toggleUpdateCampaignModal = useCallback(
        ({
            campaign,
            onClick,
            onConfirm,
        }: {
            campaign?: Campaign;
            onClick: (isModalOpen: boolean) => void;
            onConfirm?: () => void;
        }) => {
            onClick(!!campaign);
            onConfirm ? onConfirm() : setCampaign(campaign);
        },
        [setCampaign]
    );

    const isDeleteError = deleteCampaignStatus === MutationStatus.ERROR;
    const isUpdateError = updateCampaignStatusStatus === MutationStatus.ERROR;

    const isDeleteSuccess = deleteCampaignStatus === MutationStatus.SUCCESS;
    const isUpdateSuccess = updateCampaignStatusStatus === MutationStatus.SUCCESS;

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                Header: 'name',
                accessor: 'name',
                align: 'left',
                Cell: (row: any) => <Typography>{row.cell.value}</Typography>,
            },
            {
                Header: 'status',
                accessor: 'status',
                align: 'center',
                Cell: (row: any) => {
                    const campaign = row.cell.row.original;

                    return <CampaignStatusText campaign={campaign} />;
                },
            },
            {
                Header: 'schedule',
                accessor: (row) => new Date(row.startTime),
                align: 'center',
                id: 'schedule',
                sortType: 'datetime',
                Cell: (row: any) => {
                    const campaign: Campaign = row.cell.row.original;
                    const { startTime, endTime } = campaign;

                    return (
                        <>
                            {campaign.status === CampaignStatus.DRAFT ? (
                                <Typography>Not Scheduled</Typography> // show not scheduled for draft campaigns
                            ) : (
                                <>
                                    <DateRangeText startTime={startTime} endTime={endTime} />
                                    <div data-test={`${campaign.name}-recurrence`}>
                                        <Recurrence
                                            recurrenceFrequency={
                                                campaign.recurrenceFrequency as SendRecurrenceFrequency
                                            }
                                            startDatetime={startTime}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'type',
                accessor: 'channel',
                align: 'center',
                Cell: (row: any) => {
                    const { channel, abTestConfig }: Campaign = row.cell.row.original;
                    const channelName = CampaignChannelLabels[channel];
                    const hasABTest = !!abTestConfig;

                    return (
                        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                            <span>{channelName}</span>
                            {hasABTest && (
                                <Box
                                    component="span"
                                    sx={{
                                        borderRadius: '4px',
                                        border: '1px solid #9DB2FA',
                                        background: 'var(--Primary-Contrast, #FFF)',
                                        padding: '2px 6px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        lineHeight: '24px',
                                    }}
                                >
                                    A/B
                                </Box>
                            )}
                        </Box>
                    );
                },
            },
            {
                Header: '',
                accessor: 'options',
                align: 'right',
                disableSortBy: true,
                Cell: (value: any) => {
                    const campaign: Campaign = value.row.original;

                    const sharedMenuItemProps = {
                        campaign,
                        refetch,
                        resetInfoMessage: () => setInfoMessage(''),
                        setCampaign,
                        setErrorMessage,
                    };
                    const campaignNameString = `"${campaign.name}"`;
                    const cancelCampaignMenuItem = useGetCampaignMenuItem({
                        name: 'Cancel',
                        setInfoMessage: () => setInfoMessage(`Canceling ${campaignNameString}`),
                        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} canceled.`),
                        ...sharedMenuItemProps,
                    });
                    const stopCampaignMenuItem = useGetCampaignMenuItem({
                        name: 'Stop',
                        setInfoMessage: () => setInfoMessage(`Stopping ${campaignNameString}`),
                        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} stopped.`),
                        ...sharedMenuItemProps,
                    });
                    const finishCampaignMenuItem = useGetCampaignMenuItem({
                        name: 'Finish',
                        setInfoMessage: () => setInfoMessage(`Finishing ${campaignNameString}`),
                        setSuccessMessage: () => setSuccessMessage(`${campaignNameString} is finished.`),
                        ...sharedMenuItemProps,
                    });
                    const deleteCampaignMenuItem = {
                        name: 'Delete',
                        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsDeleteModalOpen }),
                    };
                    const pauseCampaignMenuItem = {
                        name: 'Pause',
                        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsPauseModalOpen }),
                        tooltipTitle: 'Temporarily stop contacts where they are in the Curation flow.',
                    };
                    const resumeCampaignMenuItem = {
                        name: 'Resume',
                        onClick: () => toggleUpdateCampaignModal({ campaign, onClick: setIsResumeModalOpen }),
                        tooltipTitle: 'Resume Curation Flow.',
                    };
                    const viewDetailsMenuItem = {
                        name: 'View Details',
                        onClick: () => history.push(`${URLPaths.CURATIONS}/${campaign.id}/summary`),
                    };
                    const editMenuItem = {
                        name: 'Edit',
                        onClick: () =>
                            history.push(`${URLPaths.CURATIONS_CUSTOM}/${campaign.id}?status=${campaign.status}`),
                        testId: 'edit-curation-menu-item',
                    };
                    // Edit option for scheduled curations - converts to draft first, then opens for editing
                    const editScheduledMenuItem = {
                        name: 'Edit',
                        disabled: isConvertingToDraft,
                        onClick: () => {
                            setCampaign(campaign);
                            setInfoMessage(`Converting ${campaignNameString} to draft for editing...`);
                            // Setting this state triggers the useEffect which calls convertToDraftAndEdit
                            setPendingEditCampaign({ id: campaign.id, name: campaign.name });
                        },
                        testId: 'edit-scheduled-curation-menu-item',
                        tooltipTitle: 'Convert to draft and edit the curation.',
                    };
                    const getMenuItems = () => {
                        const isCampaignSending = campaign.status === CampaignStatus.SENDING;
                        const isCampaignFinishing = campaign.status === CampaignStatus.FINISHING;
                        const isDraft = campaign.status === CampaignStatus.DRAFT;
                        // For draft campaigns, show Edit option as the primary action
                        if (isDraft) {
                            return [editMenuItem, deleteCampaignMenuItem];
                        }

                        if (campaign.canCancel) {
                            return [
                                viewDetailsMenuItem,
                                ...(campaign.canDraft && hasEditLiveCurationsFeatureFlag
                                    ? [editScheduledMenuItem]
                                    : []),
                                cancelCampaignMenuItem,
                                ...(campaign.canPause ? [pauseCampaignMenuItem] : []),
                            ];
                        }

                        const canShowStopWithFinishOption =
                            campaign.channel === CampaignChannels.EMAIL &&
                            !campaign.abTestConfig &&
                            campaign.canStop &&
                            isCampaignSending;

                        if (canShowStopWithFinishOption) {
                            return [
                                viewDetailsMenuItem,
                                stopCampaignMenuItem,
                                ...[finishCampaignMenuItem],
                                ...(campaign.canPause ? [pauseCampaignMenuItem] : []),
                            ];
                        }

                        // For Voice and SMS curations that can be stopped and paused
                        const isVoiceOrSmsCuration =
                            campaign.channel === CampaignChannels.VOICE_CALL ||
                            campaign.channel === CampaignChannels.SMS;
                        if (isVoiceOrSmsCuration && campaign.canStop && isCampaignSending) {
                            return [
                                viewDetailsMenuItem,
                                stopCampaignMenuItem,
                                ...(campaign.canPause ? [pauseCampaignMenuItem] : []),
                            ];
                        }
                        if (campaign.canStop && isCampaignFinishing) {
                            return [viewDetailsMenuItem, stopCampaignMenuItem];
                        }
                        if (campaign.canDelete) {
                            return [viewDetailsMenuItem, deleteCampaignMenuItem];
                        }
                        if (campaign.canResume) {
                            return [
                                viewDetailsMenuItem,
                                stopCampaignMenuItem,
                                resumeCampaignMenuItem,
                                ...(campaign.canEdit && hasEditLiveCurationsFeatureFlag ? [editMenuItem] : []),
                            ];
                        }
                        if (
                            campaign.status === CampaignStatus.SENDING_AB_TEST ||
                            campaign.status === CampaignStatus.WINNER_SCHEDULED
                        ) {
                            return [viewDetailsMenuItem, stopCampaignMenuItem];
                        }
                        return [viewDetailsMenuItem];
                    };
                    const menuItems = getMenuItems();

                    if (!menuItems?.length) return;

                    return <MoreOptionsDropdown buttonTestId={`name-${campaign.name}-options`} menuItems={menuItems} />;
                },
            },
        ],
        [
            refetch,
            toggleUpdateCampaignModal,
            history,
            hasEditLiveCurationsFeatureFlag,
            isConvertingToDraft,
            setPendingEditCampaign,
        ]
    );

    const campaignsWithTimestamp = campaigns?.map((campaign) => ({
        ...campaign,
        scheduledTimestamp: new Date(campaign.startTime),
    }));

    return (
        <>
            {isDeleteError && <ErrorSnackbar errorMessage={errorMessage} />}
            {isUpdateError && <ErrorSnackbar errorMessage={errorMessage} />}
            {infoMessage && <InfoSnackbar successMessage={infoMessage} />}
            {isDeleteSuccess && <SuccessSnackbar successMessage={successMessage} />}
            {isUpdateSuccess && <SuccessSnackbar successMessage={successMessage} />}
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Delete',
                    contentText: `You are about to delete "${campaign?.name}." This action cannot be undone.`,
                    title: 'Delete Curation',
                }}
                disabled={isDeleteLoading}
                open={isDeleteModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsDeleteModalOpen, onConfirm: deleteCampaign });
                    setInfoMessage(`Deleting "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsDeleteModalOpen })}
            />
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Pause',
                    contentText:
                        'Temporarily stop curation actions. Contacts will stay at their current step in the curation flow until the curation is resumed. No new contacts will enter the flow when paused.',
                    title: 'Pause Curation',
                }}
                disabled={isPauseLoading}
                open={isPauseModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsPauseModalOpen, onConfirm: pauseCampaign });
                    setInfoMessage(`Pausing "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsPauseModalOpen })}
            />
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Resume',
                    contentText:
                        'Resume curation actions. Contacts will resume the flow at their current step. The audience will be evaluated at the next scheduled time to pull in new contacts.',
                    title: 'Resume Curation',
                }}
                disabled={isResumeLoading}
                open={isResumeModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsResumeModalOpen, onConfirm: resumeCampaign });
                    setInfoMessage(`Resuming "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsResumeModalOpen })}
            />
            <Table
                columns={columns}
                data={campaignsWithTimestamp}
                defaultSortingRules={[{ id: 'schedule', desc: true }]}
                isRefetching={isRefetching}
                refetch={refetch}
            />
            {campaigns.length === 0 && searchTerm && <TableEmptyState searchTerm={searchTerm} />}
        </>
    );
};

export default CampaignsTable;
